<template>
  <OrCardCollectionV3
    v-if="!isLoading"
    ref="list"
    class="overflow-hidden grow p-xs md:pr-sm*"
    :items="listOfQueues"
    :loading="isRefreshing || isSearching"
    :view-modes="[]"
    view="list"
    :search-placeholder="$t('common.search')"
    data-test="queues-list"
    @searchChange="$emit('search', $event)"
  >
    <template #header>
      <queues-list-header
        class="sticky top-none z-[1]"
        data-test="head-row"
      />
    </template>
    <template #addon>
      <OrButtonV3
        variant="outlined"
        data-test="refresh-button"
        @click="$emit('refresh')"
      >
        <OrIconV3 icon="refresh" />
        {{ refreshText }}
      </OrButtonV3>
    </template>
    <template #item="{item}">
      <queues-list-item
        :key="item.id"
        :queue="item"
        :is-loading="isLoading"
        data-test="queue-item"
        @open-edit="$emit('openEdit', $event)"
        @open-info="$emit('openInfo', $event)"
        @open-monitoring="$emit('openModal', $event)"
        @delete="$emit('delete', $event)"
      />
    </template>
    <template #viewEnd>
      <div
        ref="endList"
        class="h-px w-full"
      />
    </template>
    <template #noSearchResults>
      <div
        class="flex items-center justify-center text-center typography-body-1-bold p-sm"
        data-test="no-matching-items"
      >
        {{ $t('queuesTable.noItems') }}
      </div>
    </template>
  </OrCardCollectionV3>
</template>

<script lang="ts">
import { Queue } from '../../types';
import {
  OrCardCollectionV3,
  OrButtonV3,
  OrIconV3,
  useResponsive,
} from '@onereach/ui-components';
import { defineComponent, ref, PropType } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';

import QueuesListHeader from './QueuesListHeader.vue';
import QueuesListItem from './QueuesListItem.vue';

export default defineComponent({
  name: 'QueuesCollection',
  components: {
    OrCardCollectionV3,
    OrButtonV3,
    QueuesListHeader,
    QueuesListItem,
    OrIconV3,
  },
  props: {
    items: {
      type: Array as PropType<Queue[]>,
      required: false,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSearching: {
      type: Boolean,
      required: false,
      default: false,
    },
    isRefreshing: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['openEdit', 'delete', 'openInfo', 'openModal', 'search', 'refresh', 'scroll'],
  setup() {
    const { isMobile } = useResponsive();

    const endList = ref(null);
    const endListVisible = ref(false);

    useIntersectionObserver(
      endList,
      ([{ isIntersecting }]) => {
        endListVisible.value = isIntersecting;
      },
    );
    return {
      endList,
      endListVisible,
      isMobile,
    };
  },
  data() {
    return {
      arrayOfQueues: [] as Queue[],
    };
  },
  computed: {
    listOfQueues(): Queue[] {
      return this.items;
    },
    refreshText(): string {
      return this.isMobile ? '' : this.$t('common.refresh');
    },
  },
  watch: {
    endListVisible: {
      handler: function () {
        if (this.endListVisible) this.$emit('scroll');
      },
    },
  },
});
</script>
